import { useState } from "react";
import { useForm } from "@inertiajs/react";
import { Check, Plus, DollarSign, BookOpen, Users, UserCheck, Sigma, Layers, School, DoorOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import SegmentedChoice from "@/components/shared/SegmentedChoice";
import { AWARD_STYLES, STUDENT_PRIZE_STYLES, amountFieldLabel, isAverageRanking } from "@/lib/prizes";
import { formatMoney } from "@/lib/format";

const PLACEHOLDERS = {
  campaign: { prize: "Pizza party for the whole school", money: "500", minutes: "10000" },
  classroom: { prize: "Movie afternoon for the class", money: "300", minutes: "2000" },
  student: { prize: "Fidget spinner", money: "100", minutes: "500" },
};

const KIND_OPTIONS = {
  campaign: [
    { value: "money", label: "Money raised", icon: DollarSign },
    { value: "minutes", label: "Minutes read", icon: BookOpen },
  ],
  student: [
    { value: "money", label: "Money they raise", icon: DollarSign },
    { value: "minutes", label: "Minutes they read", icon: BookOpen },
  ],
};

const STYLE_OPTIONS = {
  campaign: [
    { value: "unlock", label: "The school reaches it", icon: AWARD_STYLES.unlock.icon },
    { value: "top_classroom", label: "Top classroom", icon: AWARD_STYLES.top_classroom.icon },
  ],
  student: STUDENT_PRIZE_STYLES.map((value) => ({ value, label: AWARD_STYLES[value].label, icon: AWARD_STYLES[value].icon })),
};

const COMPETES_CHOICES = {
  classroom: { value: "classroom", label: "Each class", icon: DoorOpen },
  division: { value: "division", label: "Each division", icon: Layers },
  school: { value: "school", label: "Whole school", icon: School },
};

// Who competes, per style; the first is the default. Mirrors
// Milestone::COMPETES_WITHIN.
const COMPETES_WITHIN_OPTIONS = {
  top_classroom: [COMPETES_CHOICES.division, COMPETES_CHOICES.school],
  top_in_class: [COMPETES_CHOICES.classroom, COMPETES_CHOICES.division, COMPETES_CHOICES.school],
  raffle: [COMPETES_CHOICES.school, COMPETES_CHOICES.division],
};

// The style's choice if it offers it, otherwise its default.
function competesWithinFor(style, current) {
  const options = COMPETES_WITHIN_OPTIONS[style];
  if (!options) return "";
  return options.some((o) => o.value === current) ? current : options[0].value;
}

// "Compared by" is average vs total; an average then picks who it divides
// by. Both map onto the one `ranked_by` field.
const COMPARED_BY_OPTIONS = [
  { value: "average", label: "Average per student", icon: Users },
  { value: "total", label: "Class total", icon: Sigma },
];

const AVERAGE_OF_OPTIONS = [
  { value: "per_student", label: "Every student", icon: Users },
  { value: "per_participant", label: "Students who took part", icon: UserCheck },
];

const TOP_STYLES = ["top_in_class", "top_classroom"];

/**
 * Adds or edits one reward or student prize. The controllers convert
 * `threshold_display` (dollars or minutes, depending on kind) into the
 * stored amount — send plain form state; Inertia v2 ignores `transform:`.
 *
 * scope: "campaign" | "classroom" | "student"
 * reward: the existing reward when editing (with earned_count for prizes,
 *         won_count for top-classroom prizes)
 * goals: { money_goal_cents, minute_goal } to suggest amounts from
 */
export default function RewardForm({ scope, reward, path, onDone, onCancel, goals }) {
  const editing = !!reward;
  const isStudent = scope === "student";
  const hasStyles = !!STYLE_OPTIONS[scope];
  const { data, setData, post, patch, processing, errors, reset } = useForm({
    prize: reward?.prize ?? "",
    kind: reward?.kind ?? (isStudent ? "minutes" : "money"),
    threshold_display: reward ? String(reward.kind === "money" ? reward.threshold_amount / 100 : reward.threshold_amount) : "",
    ...(hasStyles && {
      award_style: reward?.award_style ?? "unlock",
      // Top classroom, top student and prize draws use it; unlock rewards don't.
      competes_within: reward?.competes_within ?? "",
    }),
    // Only top-classroom prizes use it; the controller clears it otherwise.
    ...(scope === "campaign" && { ranked_by: reward?.ranked_by ?? "per_student" }),
  });

  const submit = (e) => {
    e.preventDefault();
    const options = { preserveScroll: true, onSuccess: () => { if (!editing) reset(); onDone?.(); } };
    if (editing) patch(path, options);
    else post(path, options);
  };

  // Top-student and top-classroom prizes almost always want "anyone
  // who read at all" as the minimum, so prefill it rather than make admins
  // think about it.
  const chooseStyle = (style) =>
    setData((prev) => ({
      ...prev,
      award_style: style,
      competes_within: competesWithinFor(style, prev.competes_within),
      threshold_display: TOP_STYLES.includes(style) && !prev.threshold_display ? "1" : prev.threshold_display,
    }));

  const placeholders = PLACEHOLDERS[scope];
  const topClassroom = data.award_style === "top_classroom";
  const average = isAverageRanking(data.ranked_by);
  // Switching to the class total and back keeps who the average counts.
  const [averageOf, setAverageOf] = useState(isAverageRanking(reward?.ranked_by) ? reward.ranked_by : "per_student");
  const compareBy = (choice) => setData("ranked_by", choice === "total" ? "total" : averageOf);
  const chooseAverageOf = (rankedBy) => { setAverageOf(rankedBy); setData("ranked_by", rankedBy); };
  // Once anyone has won, how the prize is won can't change.
  const earned = (isStudent && reward?.earned_count > 0) || reward?.won_count > 0;
  // A draw's entries don't depend on who it's drawn from, so only a draw
  // that has picked a winner is fixed.
  const competesFixed = data.award_style === "raffle" ? reward?.draws_count > 0 : earned;
  const competesOptions = COMPETES_WITHIN_OPTIONS[data.award_style];
  const goalHint = topClassroom ? null : goalHintFor(scope, data.kind, goals);

  return (
    <form onSubmit={submit} className="space-y-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <fieldset className="space-y-4">
        <legend className="sr-only">{editing ? `Edit ${reward.prize}` : isStudent ? "New student prize" : "New reward"}</legend>

        <FormField
          name={`prize-${reward?.id ?? "new"}`}
          label="Prize"
          placeholder={topClassroom ? "Pizza party for the winning class" : placeholders.prize}
          value={data.prize}
          onChange={(e) => setData("prize", e.target.value)}
          error={errors.prize}
          required
          autoFocus={!editing}
        />

        {hasStyles && (
          <SegmentedChoice
            label={isStudent ? "How students win it" : "How it's won"}
            value={data.award_style}
            onChange={chooseStyle}
            options={STYLE_OPTIONS[scope]}
            disabled={earned}
            note={earned ? earnedNote(isStudent) : styleNote(data.award_style, data.kind, data.ranked_by, data.competes_within)}
          />
        )}
        {errors.award_style && <p className="text-sm text-destructive">{errors.award_style}</p>}
        {errors.base && <p className="text-sm text-destructive">{errors.base}</p>}

        {competesOptions && (
          <SegmentedChoice
            label={data.award_style === "raffle" ? "Draw winners from" : "Who competes"}
            value={data.competes_within}
            onChange={(competesWithin) => setData("competes_within", competesWithin)}
            options={competesOptions}
            disabled={competesFixed}
            note={competesNote(data.award_style, data.competes_within)}
          />
        )}

        <SegmentedChoice
          label="Counted by"
          value={data.kind}
          onChange={(kind) => setData("kind", kind)}
          options={KIND_OPTIONS[isStudent ? "student" : "campaign"]}
          disabled={earned && topClassroom}
        />

        {topClassroom && (
          <SegmentedChoice
            label="Classrooms are compared by"
            value={average ? "average" : "total"}
            onChange={compareBy}
            options={COMPARED_BY_OPTIONS}
            disabled={earned}
            note={average
              ? "The class total divided by the number of students, so small and large classes compete fairly."
              : "The class total. Bigger classes have an edge."}
          />
        )}

        {topClassroom && average && (
          <SegmentedChoice
            label="Who counts in the average"
            value={data.ranked_by}
            onChange={chooseAverageOf}
            options={AVERAGE_OF_OPTIONS}
            disabled={earned}
            note={averageOfNote(data.ranked_by, data.kind)}
          />
        )}

        <div className="sm:max-w-xs">
          <FormField
            name={`threshold-${reward?.id ?? "new"}`}
            label={amountFieldLabel({ scope, kind: data.kind, awardStyle: data.award_style, rankedBy: data.ranked_by })}
            type="number"
            min="1"
            step="1"
            inputMode="numeric"
            placeholder={placeholders[data.kind]}
            value={data.threshold_display}
            onChange={(e) => setData("threshold_display", e.target.value)}
            error={errors.threshold_amount}
            help={goalHint}
            required
          />
        </div>
        {topClassroom && <p className="-mt-2 text-xs leading-relaxed text-muted-foreground">{minimumNote(data.ranked_by, data.kind)}</p>}

        {isStudent && data.kind === "money" && (
          <p className="text-xs leading-relaxed text-muted-foreground">
            Worth a thought: prizes for money raised can feel different to children whose families have different resources. Reading prizes are open to everyone.
          </p>
        )}
      </fieldset>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={processing}>
          {editing ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {processing ? "Saving…" : editing ? "Save" : isStudent ? "Add prize" : "Add reward"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

// What each "Who competes" choice means for the chosen style.
function competesNote(style, competesWithin) {
  const division = competesWithin === "division";
  const setDivisions = "Set each classroom's division on its Edit page.";
  if (style === "raffle") {
    return division
      ? `You draw one winner in each division, from that division's entries. ${setDivisions}`
      : "You draw winners from the whole school, or pick the classrooms for each draw.";
  }
  if (style === "top_in_class") {
    if (division) return `The top student in each division wins, whatever their class. ${setDivisions}`;
    if (competesWithin === "school") return "One student wins for the whole school.";
    return "Every class gets its own winner.";
  }
  return division
    ? `Each division gets its own winner. ${setDivisions}`
    : "Every classroom competes for one prize, whatever its division.";
}

function earnedNote(isStudent) {
  return isStudent
    ? "Students have already earned this prize, so how it's won can't change."
    : "Classrooms have already won this prize, so how it's won can't change.";
}

function styleNote(style, kind, rankedBy, competesWithin) {
  if (style === "raffle") {
    return `Every block of ${kind === "money" ? "money raised" : "minutes read"} adds one entry to a draw. You draw winners from the prize report after reading ends.`;
  }
  if (style === "top_in_class") {
    return `The student who ${kind === "money" ? "raises the most" : "reads the most minutes"} wins. Ties all win, and winners are final when the campaign finishes.`;
  }
  if (style === "top_classroom") {
    const most = kind === "money" ? "raises the most" : "reads the most minutes";
    const where = competesWithin === "school" ? "in the school" : "in each division";
    return `The classroom that ${most}${isAverageRanking(rankedBy) ? " per student" : ""} ${where} wins. Ties all win, and winners are final when the campaign finishes.`;
  }
  return null;
}

// "Your goal is $6,000. A quarter, half and all of it make a good ladder."
function goalHintFor(scope, kind, goals) {
  if (!goals || scope === "student") return null;
  if (kind === "money" && goals.money_goal_cents) {
    return `Your goal is ${formatMoney(goals.money_goal_cents)}. A quarter, half and all of it make a good ladder.`;
  }
  if (kind === "minutes" && goals.minute_goal) {
    return `Your goal is ${goals.minute_goal.toLocaleString("en-CA")} minutes.`;
  }
  return null;
}

// Every student on the roster, or only those with something counted.
function averageOfNote(rankedBy, kind) {
  const nothing = kind === "money" ? "haven't raised anything" : "haven't logged any reading";
  return rankedBy === "per_participant"
    ? `Students who ${nothing} are left out, so a class where a few students do a lot can win.`
    : `Students who ${nothing} count as 0, so the class does best when everyone takes part.`;
}

// What the qualifying minimum is for: a floor on the class's score, not a
// filter on students.
function minimumNote(rankedBy, kind) {
  const score = isAverageRanking(rankedBy) ? "average" : "total";
  const nobody = kind === "money" ? "nobody has raised anything" : "nobody has read";
  return `The class's ${score} has to reach this to win. 1 means "took part at all", so where ${nobody} yet there's no winner. It's a bar for the class, not for each student.`;
}
